<template>
  <div class="generate-subhire-document-modal">
    <div
      v-if="!subhire"
      class="p-4 text-center text-gray-500"
    >
      <p>No subhire data available</p>
    </div>
    <template v-else>
      <div class="mb-6">
        <h3 class="text-xl font-semibold mb-2">
          Generate Document
        </h3>
        <p class="text-sm text-surface-600 dark:text-surface-400">
          Subhire: <strong>{{ subhire.name || subhire.subhire_number || (subhire.id ? `SH-${subhire.id}` : 'N/A') }}</strong><span
            v-if="subhire.name && (subhire.subhire_number || subhire.id)"
            class="text-surface-500"
          > ({{ subhire.subhire_number || `SH-${subhire.id}` }})</span>
        </p>
        <p class="text-sm text-surface-600 dark:text-surface-400">
          Supplier: <strong>{{ subhire.supplier?.company_name || subhire.supplier?.name || 'Unknown' }}</strong>
        </p>
      </div>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2">Message (for email and supplier page)</label>
          <TextArea
            v-model="quoteForm.message"
            rows="4"
            placeholder="Optional message to include in the email and on the supplier response page..."
            class="w-full"
          />
          <p class="text-xs text-surface-500 dark:text-surface-400 mt-1">
            Markdown is supported. Leave blank to send the request without a custom message.
          </p>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-6 pt-4 border-t">
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          @click="handleCancel"
        />
        <Button
          label="Generate PDF"
          icon="pi pi-file-pdf"
          :loading="isGenerating && actionType === 'pdf'"
          @click="handleGeneratePdf"
        />
        <Button
          label="Send request to supplier"
          icon="pi pi-send"
          :loading="isGenerating && actionType === 'quote'"
          @click="handleGenerateQuote"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useToastService } from '@/composables/useToastService';
import { projectStore } from '@/components/project/officeProject/projectContents/projectStore';
import { sendSubhireRequest } from '@/services/subhireRequestsService';

const props = defineProps({
  subhire: {
    type: Object,
    required: false,
    default: () => null
  },
  projectId: {
    type: [String, Number],
    required: false,
    default: null
  },
  equipmentListId: {
    type: [String, Number],
    required: false,
    default: null
  }
});

const emit = defineEmits(['close', 'generated']);

const toast = useToastService();

const isGenerating = ref(false);
const actionType = ref(null);

const quoteForm = ref({
  message: ''
});

const handleGeneratePdf = async () => {
  isGenerating.value = true;
  actionType.value = 'pdf';

  try {
    // TODO: Implement PDF generation API call

    toast.add({
      severity: 'success',
      summary: 'PDF Generated',
      detail: 'PDF document has been generated successfully',
      life: 3000
    });

    emit('generated', {
      type: 'pdf',
      data: quoteForm.value
    });

    handleCancel();
  } catch (error) {
    console.error('Error generating PDF:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to generate PDF. Please try again.',
      life: 3000
    });
  } finally {
    isGenerating.value = false;
    actionType.value = null;
  }
};

const handleGenerateQuote = async () => {
  if (!props.subhire?.id) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Subhire is required to send the request.',
      life: 3000
    });
    return;
  }

  const projectId = props.projectId ?? projectStore.currentProjectId;
  const equipmentListId = props.equipmentListId ?? projectStore.currentEquipmentListId;
  if (!projectId || !equipmentListId) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Project and equipment list are required. Please select an equipment list in the subhires view.',
      life: 3000
    });
    return;
  }

  isGenerating.value = true;
  actionType.value = 'quote';

  try {
    const lineIds = (props.subhire.lines || []).map((line) => line.id).filter(Boolean);
    await sendSubhireRequest(projectId, equipmentListId, props.subhire.id, {
      message: quoteForm.value.message?.trim() || undefined,
      line_ids: lineIds.length ? lineIds : undefined
    });

    toast.add({
      severity: 'success',
      summary: 'Request sent',
      detail: 'The supplier will receive an email with a link to respond to this subhire request.',
      life: 3000
    });

    emit('generated', {
      type: 'quote',
      data: quoteForm.value
    });

    handleCancel();
  } catch (error) {
    const msg =
      error?.response?.data?.message ??
      error?.response?.data?.error ??
      (typeof error?.response?.data?.errors === 'string' ? error.response.data.errors : null) ??
      error?.message ??
      'Failed to send subhire request.';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: msg,
      life: 5000
    });
  } finally {
    isGenerating.value = false;
    actionType.value = null;
  }
};

const handleCancel = () => {
  emit('close');
};
</script>

<style scoped>
.generate-subhire-document-modal {
  padding: 1rem;
}

</style>
